/**
 * Ambient life for Animus Quarter's High Street — wandering pedestrians and a
 * few parked cars. Without them the street reads as a film set: correct
 * geometry, nobody home.
 *
 * Two kinds of life, one module:
 *
 *  1. Pedestrian NPCs stroll the FOOTWAYS (never the carriageway), pausing and
 *     turning to face where they walk. They are FAKE ambient figures — not the
 *     real multiplayer presence avatars (see world/players.ts). They reuse the
 *     authored character rigs and are driven each frame from a pure wander
 *     state machine seeded per index so the crowd never marches in lockstep.
 *
 *  2. Parked cars sit along the carriageway edge, giving the road a scale and a
 *     reason to exist. Static props with colliders, scaled from their measured
 *     bounding box (the Kenney Car Kit is roughly metric, so this is a gentle
 *     ~1.27x to human-car width, NOT the ×8 the city-kit BUILDINGS need).
 *
 * COORDINATE SPACE: the district is built on its own root under `app.root`.
 * `spawnCharacter` initially parents to `worldRoot(app)`, so each rig is
 * immediately re-parented beneath the district root before it is positioned.
 */
import * as pc from 'playcanvas'
import {
  HUMAN_VARIANTS,
  HUMAN_WALK_CLIP,
  humanIdleClipFor,
  playClip,
  spawnCharacter,
  walkYawDeg,
} from '../characters'
import { getCachedAsset, onAssetReady } from '../assets'
import { registerCollider } from '../colliders'
import {
  HIGH_ST_MIN_X, HIGH_ST_MAX_X,
  HIGH_ST_NORTH_FACE, HIGH_ST_SOUTH_FACE,
  CARRIAGEWAY_MIN_Z, CARRIAGEWAY_MAX_Z,
  STEM_MIN_X, STEM_MAX_X,
  PLOTS, plotDoorPosition, DOOR_WIDTH,
} from './district'

// ── Tuning ──────────────────────────────────────────────────────────────────

export type Side = 'north' | 'south'

/** Pedestrian walk speed. Real footway pace; the player moves at 2.6 m/s. */
export const NPC_SPEED = 1.3
/** Default pedestrian count spread across the two footways. */
export const NPC_COUNT = 5

/** Clear band kept back from the building line (doors + fascia recesses). */
export const FACE_CLEARANCE = 1.2
/** Clear band kept back from the carriageway edge (past the tree/lamp line). */
export const KERB_CLEARANCE = 1.6
/** Half a doorway's no-walk column: half the leaf plus breathing room. */
export const DOORWAY_CLEARANCE = 1.5
/** Kept out of the stem mouth so the composed vista up the stem stays open. */
export const JUNCTION_CLEARANCE = 3
/** Kept off the very ends of the High Street (backdrop blocks / perimeter). */
export const END_MARGIN = 2

/** Distance to a waypoint at which a walker is treated as arrived. */
export const ARRIVE_EPS = 0.2
/** Chance a walker pauses (rather than picking a new waypoint) on arrival. */
export const PAUSE_CHANCE = 0.35
export const PAUSE_MIN_S = 1.5
export const PAUSE_MAX_S = 4.5

// ── Deterministic RNG ─────────────────────────────────────────────────────────

/** mulberry32 — a tiny, fast, well-distributed seeded PRNG. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** A distinct seed per NPC index so their wanders never synchronise. */
export function seedForIndex(i: number): number {
  return ((i + 1) * 0x9e3779b1) >>> 0
}

// ── Footway geometry ──────────────────────────────────────────────────────────

export interface Band {
  side: Side
  minX: number
  maxX: number
  /** Z of the walking band, inset from both the building line and the kerb. */
  minZ: number
  maxZ: number
}

/**
 * The walkable band on one footway: the full footway rectangle, inset off the
 * building face and off the kerb so pedestrians clear both the doorways and the
 * kerb-side tree/lamp line.
 */
export function footwayBand(side: Side): Band {
  const minX = HIGH_ST_MIN_X + END_MARGIN
  const maxX = HIGH_ST_MAX_X - END_MARGIN
  if (side === 'north') {
    // Footway z ∈ [NORTH_FACE, CARRIAGEWAY_MIN_Z]; inset toward mid-footway.
    return { side, minX, maxX, minZ: HIGH_ST_NORTH_FACE + FACE_CLEARANCE, maxZ: CARRIAGEWAY_MIN_Z - KERB_CLEARANCE }
  }
  // Footway z ∈ [CARRIAGEWAY_MAX_Z, SOUTH_FACE]; inset toward mid-footway.
  return { side, minX, maxX, minZ: CARRIAGEWAY_MAX_Z + KERB_CLEARANCE, maxZ: HIGH_ST_SOUTH_FACE - FACE_CLEARANCE }
}

/** Plots whose frontage (and door) opens onto this footway. */
function plotsOnSide(side: Side): typeof PLOTS[number][] {
  return PLOTS.filter((p) =>
    p.enterable && (side === 'north' ? p.side === 'north' : p.side !== 'north'),
  )
}

export interface Interval {
  lo: number
  hi: number
}

/** Subtract [a,b] from a set of disjoint, sorted intervals. Pure. */
function subtract(intervals: Interval[], a: number, b: number): Interval[] {
  const out: Interval[] = []
  for (const iv of intervals) {
    if (b <= iv.lo || a >= iv.hi) { out.push(iv); continue } // no overlap
    if (a > iv.lo) out.push({ lo: iv.lo, hi: a })
    if (b < iv.hi) out.push({ lo: b, hi: iv.hi })
  }
  return out.filter((iv) => iv.hi - iv.lo > 1e-6)
}

/**
 * The X ranges a pedestrian may occupy on one footway: the along-street span
 * minus every doorway column and, on the south side, the stem-mouth sightline.
 * Guaranteeing waypoints land in these intervals is what keeps NPCs clear of
 * doorways and the junction without any rejection sampling.
 */
export function allowedXIntervals(side: Side): Interval[] {
  const band = footwayBand(side)
  let intervals: Interval[] = [{ lo: band.minX, hi: band.maxX }]
  if (side === 'south') {
    intervals = subtract(intervals, STEM_MIN_X - JUNCTION_CLEARANCE, STEM_MAX_X + JUNCTION_CLEARANCE)
  }
  for (const plot of plotsOnSide(side)) {
    const [doorX] = plotDoorPosition(plot)
    const half = DOOR_WIDTH / 2 + DOORWAY_CLEARANCE
    intervals = subtract(intervals, doorX - half, doorX + half)
  }
  return intervals
}

export interface Point {
  x: number
  z: number
}

/**
 * A random valid waypoint on a footway: a length-weighted pick among the
 * allowed X intervals, uniform within it, at a uniform Z across the band. Pure
 * given `rng`.
 */
export function pickWaypoint(rng: () => number, band: Band, allowedX: Interval[]): Point {
  const total = allowedX.reduce((s, iv) => s + (iv.hi - iv.lo), 0)
  let pickX = band.minX
  if (total > 0) {
    let r = rng() * total
    for (const iv of allowedX) {
      const w = iv.hi - iv.lo
      if (r <= w) { pickX = iv.lo + r; break }
      r -= w
    }
  }
  const z = band.minZ + rng() * (band.maxZ - band.minZ)
  return { x: pickX, z }
}

// ── Footway / doorway predicates (exported for tests) ─────────────────────────

/** True if (x,z) is on the High Street carriageway. */
export function isInCarriageway(x: number, z: number): boolean {
  return x >= HIGH_ST_MIN_X && x <= HIGH_ST_MAX_X &&
    z >= CARRIAGEWAY_MIN_Z && z <= CARRIAGEWAY_MAX_Z
}

/** True if (x,z) is on either High Street footway. */
export function isOnFootway(x: number, z: number): boolean {
  if (x < HIGH_ST_MIN_X || x > HIGH_ST_MAX_X) return false
  const north = z >= HIGH_ST_NORTH_FACE && z <= CARRIAGEWAY_MIN_Z
  const south = z >= CARRIAGEWAY_MAX_Z && z <= HIGH_ST_SOUTH_FACE
  return north || south
}

/**
 * True if (x,z) sits in the no-walk column in front of any building door — the
 * threshold a pedestrian standing there would block.
 */
export function isNearDoorway(x: number, z: number): boolean {
  for (const plot of PLOTS) {
    if (!plot.enterable) continue
    const [doorX, doorZ] = plotDoorPosition(plot)
    if (Math.abs(x - doorX) < DOOR_WIDTH / 2 + DOORWAY_CLEARANCE &&
        Math.abs(z - doorZ) < FACE_CLEARANCE + 0.5) {
      return true
    }
  }
  return false
}

/** True if x falls in the stem-mouth sightline that must stay open. */
export function isInJunctionSightline(x: number): boolean {
  return x > STEM_MIN_X - JUNCTION_CLEARANCE && x < STEM_MAX_X + JUNCTION_CLEARANCE
}

// ── Wander state machine (pure) ───────────────────────────────────────────────

export interface Walker {
  side: Side
  x: number
  z: number
  targetX: number
  targetZ: number
  /** World yaw (deg) — 0 faces +Z, matching walkYawDeg / the character rig. */
  yawDeg: number
  /** True while standing still (idle clip); false while walking. */
  paused: boolean
  /** Remaining pause time (s) while `paused`. */
  pauseTimer: number
  /** Whether the walker is currently moving (drives walk vs idle clip). */
  moving: boolean
}

export interface WalkerCtx {
  rng: () => number
  band: Band
  allowedX: Interval[]
}

/** Random pause duration in [PAUSE_MIN_S, PAUSE_MAX_S]. */
function pauseDuration(rng: () => number): number {
  return PAUSE_MIN_S + rng() * (PAUSE_MAX_S - PAUSE_MIN_S)
}

function retarget(w: Walker, ctx: WalkerCtx): Walker {
  const next = pickWaypoint(ctx.rng, ctx.band, ctx.allowedX)
  return {
    ...w,
    targetX: next.x,
    targetZ: next.z,
    yawDeg: walkYawDeg([w.x, 0, w.z], [next.x, 0, next.z]),
    paused: false,
    pauseTimer: 0,
    moving: true,
  }
}

/**
 * Advance a walker by `dt`. Pure: returns a NEW state, given the same `ctx.rng`
 * draws. Walks straight toward its waypoint at NPC_SPEED; on arrival it either
 * pauses (idle) or picks a fresh waypoint. A paused walker counts down, then
 * heads off again. It never leaves its footway band because every waypoint is
 * drawn from it.
 */
export function stepWalker(w: Walker, dt: number, ctx: WalkerCtx): Walker {
  if (w.paused) {
    const pauseTimer = w.pauseTimer - dt
    if (pauseTimer > 0) return { ...w, pauseTimer, moving: false }
    return retarget(w, ctx)
  }

  const dx = w.targetX - w.x
  const dz = w.targetZ - w.z
  const dist = Math.hypot(dx, dz)
  const stepLen = NPC_SPEED * dt

  if (dist <= ARRIVE_EPS || dist <= stepLen) {
    // Arrived. Snap to the waypoint, then pause or move on.
    const arrived: Walker = { ...w, x: w.targetX, z: w.targetZ, moving: false }
    if (ctx.rng() < PAUSE_CHANCE) {
      return { ...arrived, paused: true, pauseTimer: pauseDuration(ctx.rng) }
    }
    return retarget(arrived, ctx)
  }

  const f = stepLen / dist
  return {
    ...w,
    x: w.x + dx * f,
    z: w.z + dz * f,
    yawDeg: walkYawDeg([w.x, 0, w.z], [w.targetX, 0, w.targetZ]),
    moving: true,
  }
}

/** Build the initial walker for NPC `index`, seeded deterministically. */
export function makeWalker(index: number): { walker: Walker; ctx: WalkerCtx } {
  const rng = mulberry32(seedForIndex(index))
  const side: Side = index % 2 === 0 ? 'north' : 'south'
  const band = footwayBand(side)
  const allowedX = allowedXIntervals(side)
  const ctx: WalkerCtx = { rng, band, allowedX }
  const start = pickWaypoint(rng, band, allowedX)
  const next = pickWaypoint(rng, band, allowedX)
  const walker: Walker = {
    side,
    x: start.x,
    z: start.z,
    targetX: next.x,
    targetZ: next.z,
    yawDeg: walkYawDeg([start.x, 0, start.z], [next.x, 0, next.z]),
    paused: false,
    pauseTimer: 0,
    moving: true,
  }
  return { walker, ctx }
}

// ── Parked cars ───────────────────────────────────────────────────────────────

export interface CarSpec {
  file: string
  /** Measured natural bounding size (max−min) in the model's own units. */
  natural: readonly [number, number, number]
}

/** Measured from the Draco-compressed GLBs (Kenney Car Kit, roughly metric). */
export const CAR_SPECS: Readonly<Record<string, CarSpec>> = {
  sedan:    { file: 'car_sedan.glb',    natural: [1.5, 1.3, 2.55] },
  van:      { file: 'car_van.glb',      natural: [1.5, 1.35, 2.75] },
  taxi:     { file: 'car_taxi.glb',     natural: [1.5, 1.5, 2.75] },
  delivery: { file: 'car_delivery.glb', natural: [1.5, 1.65, 3.25] },
}

/** Real small-car width. Every kit car is 1.5 wide, so this is a ~1.27x scale. */
export const CAR_TARGET_WIDTH = 1.9

/** Uniform scale bringing a car to a believable width without distorting it. */
export function carScale(spec: CarSpec): number {
  return CAR_TARGET_WIDTH / spec.natural[0]
}

export interface ParkedCar {
  id: string
  kind: keyof typeof CAR_SPECS
  x: number
  z: number
  /** 90 or 270 — the car's length lies along the street (X). */
  rotY: 90 | 270
}

/**
 * A handful of cars along the carriageway edge — parked just off each kerb,
 * lengthwise along the street, clear of the stem mouth so they never intrude on
 * the composed vista up to the anchor.
 */
export function computeParkedCars(): ParkedCar[] {
  const northZ = CARRIAGEWAY_MIN_Z + 0.9
  const southZ = CARRIAGEWAY_MAX_Z - 0.9
  return [
    { id: 'car-n-w', kind: 'sedan',    x: -23, z: northZ, rotY: 90 },
    { id: 'car-n-e', kind: 'van',      x: 25,  z: northZ, rotY: 270 },
    { id: 'car-s-w', kind: 'taxi',     x: -25, z: southZ, rotY: 90 },
    { id: 'car-s-e', kind: 'delivery', x: -35, z: southZ, rotY: 270 },
  ]
}

/**
 * Ground footprint of a parked car after scaling and its quarter-turn. With the
 * length along X (rotY 90/270), world X spans the car length and world Z its
 * width. Pure — used for both colliders and tests.
 */
export function carFootprint(car: ParkedCar): {
  minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number
} {
  const spec = CAR_SPECS[car.kind]
  const s = carScale(spec)
  const halfLen = (spec.natural[2] * s) / 2
  const halfWid = (spec.natural[0] * s) / 2
  const height = spec.natural[1] * s
  return {
    minX: car.x - halfLen, maxX: car.x + halfLen,
    minY: 0, maxY: height,
    minZ: car.z - halfWid, maxZ: car.z + halfWid,
  }
}

// ── Engine wiring ─────────────────────────────────────────────────────────────

export interface DistrictLife {
  /** Advance the pedestrians. Cars are static. Call once per frame. */
  frame(dt: number): void
  destroy(): void
}

interface NpcRuntime {
  seed: string
  entity: pc.Entity
  destroy(): void
  walker: Walker
  ctx: WalkerCtx
  idleElapsed: number
}

/**
 * Populate the district with ambient pedestrians and parked cars. Everything is
 * parented to `districtRoot` (district space), NEVER worldRoot(app) — which is
 * pinned to the office interior. Returns a handle whose `frame(dt)` drives the
 * pedestrians; call it from the main update loop.
 */
export function spawnDistrictLife(
  app: pc.Application,
  districtRoot: pc.Entity,
  count = NPC_COUNT,
): DistrictLife {
  // ── Pedestrians ──
  const npcs: NpcRuntime[] = []
  for (let i = 0; i < count; i++) {
    const { walker, ctx } = makeWalker(i)
    const seed = `npc-${i}`
    const handle = spawnCharacter(
      app,
      HUMAN_VARIANTS[i % HUMAN_VARIANTS.length],
      [walker.x, 0, walker.z],
      seed,
      { castShadows: false },
    )
    districtRoot.addChild(handle.entity)
    handle.entity.setPosition(walker.x, 0, walker.z)
    handle.entity.setEulerAngles(0, walker.yawDeg, 0)
    npcs.push({
      seed,
      entity: handle.entity,
      destroy: handle.destroy,
      walker,
      ctx,
      idleElapsed: 0,
    })
  }

  // ── Parked cars ──
  const carHolders: pc.Entity[] = []
  for (const car of computeParkedCars()) {
    const spec = CAR_SPECS[car.kind]
    const s = carScale(spec)
    const holder = new pc.Entity(`car-${car.id}`)
    holder.setLocalScale(s, s, s)
    holder.setPosition(car.x, 0, car.z)
    holder.setEulerAngles(0, car.rotY, 0)
    districtRoot.addChild(holder)
    carHolders.push(holder)

    const attach = (asset: pc.Asset): void => {
      if (!asset.resource) return
      const container = asset.resource as pc.ContainerResource
      // Static cars already read through their PBR shading and ground contact.
      // Four multi-mesh models in the cascaded shadow map cost more passes than
      // the entire dynamically batched ambient crowd.
      holder.addChild(container.instantiateRenderEntity({ castShadows: false }))
    }
    const cached = getCachedAsset(spec.file)
    if (cached) attach(cached)
    else onAssetReady(spec.file, attach)

    registerCollider(carFootprint(car))
  }

  return {
    frame(dt: number): void {
      for (const npc of npcs) {
        npc.walker = stepWalker(npc.walker, dt, npc.ctx)
        npc.entity.setPosition(npc.walker.x, 0, npc.walker.z)
        npc.entity.setEulerAngles(0, npc.walker.yawDeg, 0)
        if (npc.walker.moving) {
          npc.idleElapsed = 0
          playClip(npc.entity, HUMAN_WALK_CLIP)
        } else {
          npc.idleElapsed += dt
          playClip(npc.entity, humanIdleClipFor(npc.seed, npc.idleElapsed))
        }
      }
    },
    destroy(): void {
      for (const npc of npcs) npc.destroy()
      for (const holder of carHolders) holder.destroy()
    },
  }
}
